import type { Mark, Node as ProseMirrorNode } from '@tiptap/pm/model';
import { getMentionExportText } from './export-script-format';

export const isMentionNode = (node: ProseMirrorNode): boolean =>
  node.type.name === 'mention' || node.type.name === 'scriptMention';

export const getLeafNodePlainText = (node: ProseMirrorNode): string => {
  if (node.type.name === 'hardBreak') {
    return '\n';
  }
  if (isMentionNode(node)) {
    return getMentionExportText({
      type: node.type.name,
      attrs: node.attrs as {
        label?: unknown;
        id?: unknown;
        mentionSuggestionChar?: unknown;
      },
    });
  }
  return '';
};

export const getDocumentPlainText = (
  doc: ProseMirrorNode,
  from: number = 0,
  to: number = doc.content.size,
  blockSeparator: string = '\n',
): string => doc.textBetween(from, to, blockSeparator, getLeafNodePlainText);

type InlineFormatFlags = {
  bold: boolean;
  italic: boolean;
  strike: boolean;
};

const EMPTY_FORMAT_FLAGS: InlineFormatFlags = {
  bold: false,
  italic: false,
  strike: false,
};

const flagsFromMarks = (marks: readonly Mark[]): InlineFormatFlags => ({
  bold: marks.some((mark) => mark.type.name === 'bold'),
  italic: marks.some((mark) => mark.type.name === 'italic'),
  strike: marks.some((mark) => mark.type.name === 'strike'),
});

const sameFormatFlags = (left: InlineFormatFlags, right: InlineFormatFlags): boolean =>
  left.bold === right.bold
  && left.italic === right.italic
  && left.strike === right.strike;

const wrapTextWithMarkdown = (text: string, flags: InlineFormatFlags): string => {
  if (!text || (!flags.bold && !flags.italic && !flags.strike)) {
    return text;
  }
  let wrapped = text;
  if (flags.strike) {
    wrapped = `~~${wrapped}~~`;
  }
  if (flags.bold && flags.italic) {
    wrapped = `***${wrapped}***`;
  } else if (flags.bold) {
    wrapped = `**${wrapped}**`;
  } else if (flags.italic) {
    wrapped = `*${wrapped}*`;
  }
  return wrapped;
};

export const getDocumentMarkdownText = (
  doc: ProseMirrorNode,
  from: number = 0,
  to: number = doc.content.size,
  blockSeparator: string = '\n',
): string => {
  type Segment = { text: string; flags: InlineFormatFlags };
  const segments: Segment[] = [];
  let openTextblock = false;

  doc.nodesBetween(from, to, (node, pos) => {
    if (node.isTextblock) {
      if (openTextblock) {
        segments.push({ text: blockSeparator, flags: { ...EMPTY_FORMAT_FLAGS } });
      }
      openTextblock = true;
      return true;
    }

    if (node.isText && node.text) {
      const start = Math.max(from, pos);
      const end = Math.min(to, pos + node.text.length);
      if (start < end) {
        const slice = node.text.slice(start - pos, end - pos);
        if (slice) {
          segments.push({ text: slice, flags: flagsFromMarks(node.marks) });
        }
      }
      return false;
    }

    if (node.type.name === 'hardBreak' && pos >= from && pos < to) {
      segments.push({ text: '\n', flags: { ...EMPTY_FORMAT_FLAGS } });
      return false;
    }

    if (isMentionNode(node) && pos >= from && pos < to) {
      const mentionText = getLeafNodePlainText(node);
      if (mentionText) {
        segments.push({ text: mentionText, flags: flagsFromMarks(node.marks) });
      }
      return false;
    }

    return true;
  });

  const merged: Segment[] = [];
  for (const segment of segments) {
    const last = merged[merged.length - 1];
    if (last && sameFormatFlags(last.flags, segment.flags)) {
      last.text += segment.text;
      continue;
    }
    merged.push({ text: segment.text, flags: { ...segment.flags } });
  }

  return merged.map((segment) => wrapTextWithMarkdown(segment.text, segment.flags)).join('');
};

export type MentionInRange = {
  plainText: string;
  node: ProseMirrorNode;
  pos: number;
};

export const collectMentionsInRange = (
  doc: ProseMirrorNode,
  from: number,
  to: number,
): MentionInRange[] => {
  const mentions: MentionInRange[] = [];
  doc.nodesBetween(from, to, (node, pos) => {
    if (!isMentionNode(node)) {
      return;
    }
    const plainText = getLeafNodePlainText(node);
    if (!plainText) {
      return;
    }
    mentions.push({ plainText, node, pos });
  });
  return mentions;
};

export type SuggestionContentSegment =
  | { type: 'text'; text: string }
  | { type: 'mention'; node: ProseMirrorNode };

export const segmentSuggestionTextWithMentions = (
  suggestionText: string,
  mentions: MentionInRange[],
): SuggestionContentSegment[] => {
  if (!suggestionText) {
    return [];
  }
  if (mentions.length === 0) {
    return [{ type: 'text', text: suggestionText }];
  }

  const uniqueMentions = [...mentions].sort(
    (a, b) => b.plainText.length - a.plainText.length || a.pos - b.pos,
  );
  const usedMentionPositions = new Set<number>();
  const segments: SuggestionContentSegment[] = [];
  let cursor = 0;

  while (cursor < suggestionText.length) {
    let matched: MentionInRange | null = null;
    for (const mention of uniqueMentions) {
      if (usedMentionPositions.has(mention.pos)) {
        continue;
      }
      if (suggestionText.startsWith(mention.plainText, cursor)) {
        matched = mention;
        break;
      }
    }

    if (matched) {
      usedMentionPositions.add(matched.pos);
      segments.push({ type: 'mention', node: matched.node });
      cursor += matched.plainText.length;
      continue;
    }

    const nextMentionIndex = uniqueMentions.reduce((earliest, mention) => {
      if (usedMentionPositions.has(mention.pos)) {
        return earliest;
      }
      const index = suggestionText.indexOf(mention.plainText, cursor);
      if (index < 0) {
        return earliest;
      }
      return earliest < 0 ? index : Math.min(earliest, index);
    }, -1);

    const textEnd = nextMentionIndex < 0 ? suggestionText.length : nextMentionIndex;
    if (textEnd > cursor) {
      segments.push({ type: 'text', text: suggestionText.slice(cursor, textEnd) });
      cursor = textEnd;
    } else {
      segments.push({ type: 'text', text: suggestionText[cursor]! });
      cursor += 1;
    }
  }

  return segments;
};
